(function (root, factory) {
  "use strict";
  const api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  if (root && root.document) {
    const start = () => root.document.querySelectorAll('[data-ta-express]').forEach(node => api.mount(node, root));
    if (root.document.readyState === "loading") root.document.addEventListener("DOMContentLoaded", start, { once: true });
    else start();
  }
})(typeof window !== "undefined" ? window : null, function () {
  "use strict";
  const API_BASE = "https://api.teleadapt.com";
  const HANDLE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const HEX = /^[a-f0-9]{10}$/;
  const TURNSTILE_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  const AMBIGUOUS = "We could not confirm whether your request was received. Please do not submit repeatedly. Contact TeleAdapt if you need assistance.";
  const COUNTRY_CODES = "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW".split(" ");
  const COUNTRY_ALIASES = {
    GB: ["UK", "U.K.", "Great Britain", "Britain", "England", "Scotland", "Wales", "Northern Ireland"],
    US: ["US", "USA", "U.S.", "America"],
    KR: ["South Korea", "Korea, Republic of"],
    KP: ["North Korea", "Korea, Democratic People's Republic of"],
    RU: ["Russia", "Russian Federation"],
    CZ: ["Czech Republic"],
    TR: ["Turkey"],
    AE: ["UAE"],
    BO: ["Bolivia"],
    BN: ["Brunei"],
    CD: ["Democratic Republic of the Congo", "DR Congo"],
    CG: ["Republic of the Congo"],
    CI: ["Ivory Coast"],
    LA: ["Laos"],
    MD: ["Moldova"],
    TW: ["Taiwan"],
    TZ: ["Tanzania"],
    VA: ["Vatican City"],
    VN: ["Vietnam"]
  };
  const US_OFFICE_CODES = new Set("US CA MX BZ CR SV GT HN NI PA AR BO BR CL CO EC FK GF GS GY PE PY SR UY VE AI AG AW BS BB BM BQ VG KY CU CW DM DO GD GP HT JM MQ MS PR BL KN LC MF VC SX TT TC VI".split(" "));
  function normaliseCountry(value) {
    return String(value || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }

  function countryIndex(displayNames) {
    if (!displayNames) {
      if (typeof Intl !== "object" || typeof Intl.DisplayNames !== "function") {
        throw new Error("Country names are unavailable in this browser.");
      }
      displayNames = new Intl.DisplayNames(["en"], { type: "region" });
    }
    return COUNTRY_CODES.map(function (code) {
      var name = displayNames.of(code);
      if (!name || name === code) {
        throw new Error("A canonical country name is unavailable.");
      }
      return {
        code: code,
        name: name,
        aliases: COUNTRY_ALIASES[code] || [],
        assigned_sales_office: US_OFFICE_CODES.has(code) ? "us" : "china"
      };
    });
  }

  function resolveCountry(value, countries) {
    var term = normaliseCountry(value);
    var matches;
    if (!term) return null;
    matches = countries.filter(function (country) {
      return [country.name].concat(country.aliases).some(function (name) {
        return normaliseCountry(name) === term;
      });
    });
    return matches.length === 1 ? matches[0] : null;
  }

  const optional = value => typeof value === 'string' && value.trim() ? value.trim() : null;
  function invocation(search) {
    const params = new URLSearchParams(search);
    const products = params.getAll('product');
    const product = (products[0] || '').trim().toLowerCase();
    const mc_eid = (params.get('mc_eid') || '').trim().toLowerCase();
    const mc_cid = (params.get('mc_cid') || '').trim().toLowerCase();
    if (products.length !== 1 || params.has('compare') || !HANDLE.test(product) || !HEX.test(mc_eid) || !HEX.test(mc_cid)) throw Error('Invalid link');
    return { product, mc_eid, mc_cid };
  }
  async function request(fetcher, url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetcher(url, { ...options, signal: controller.signal, redirect: 'error' });
      const body = await response.json().catch(() => null);
      return { response, body };
    } finally { clearTimeout(timer); }
  }
  async function lookupProduct(fetcher, origin, routesRoot, handle) {
    if (!HANDLE.test(handle)) throw Error('Invalid handle');
    const base = new URL(routesRoot || '/', origin);
    if (base.origin !== origin || base.search || base.hash) throw Error('Invalid Shopify root');
    if (!base.pathname.endsWith('/')) base.pathname += '/';
    const url = new URL(`products/${encodeURIComponent(handle)}.json`, base);
    const { response, body } = await request(fetcher, url.href, { headers: { Accept: 'application/json' } });
    const product = body?.product;
    if (!response.ok || !body || Array.isArray(body) || !product || Array.isArray(product)
      || product.handle !== handle || !optional(product.title)) throw Error('Product unavailable');
    return Object.freeze({ name: product.title.trim(), handle: product.handle, url: `/products/${product.handle}` });
  }
  function validResolution(body, link) {
    const nullable = value => value === null || typeof value === 'string';
    return Boolean(body && body.success === true && body.result === 'express_quotation_resolved'
      && body.product?.handle === link.product && body.contact
      && nullable(body.contact.first_name) && nullable(body.contact.last_name)
      && nullable(body.contact.company) && typeof body.contact.email === 'string'
      && body.country === null
      && ['subscribed', 'unsubscribed', 'transactional', 'archived', 'pending'].includes(body.mailchimp?.member_status)
      && body.marketing_action === 'no_change'
      && body.attribution?.invocation_source === 'mailchimp' && body.attribution?.selection_source === 'mailchimp'
      && body.attribution.campaign?.provider === 'mailchimp'
      && body.attribution.campaign.identifier === link.mc_cid && nullable(body.attribution.campaign.name));
  }
  async function resolveContact(fetcher, apiBase, link) {
    if (apiBase !== API_BASE) throw Error('Unsupported API configuration');
    const { response, body } = await request(fetcher, `${apiBase}/api/v1/express-quotation/resolve`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(link)
    });
    if (!response.ok || !validResolution(body, link)) throw Error('Express unavailable');
    return body;
  }
  function selectCountry(countries, code) {
    return countries.find(country => country.code === String(code || '').trim().toUpperCase()) || null;
  }
  function contactErrors(values, countries) {
    const errors = {};
    for (const name of ['first_name', 'last_name']) if (!optional(values[name])) errors[name] = 'This field is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(values.email || '').trim())) errors.email = 'Enter a valid email address.';
    if (!selectCountry(countries, values.country)) errors.country = 'Choose a country.';
    return errors;
  }
  function errorsFor(values, countries) {
    const errors = contactErrors(values, countries);
    for (const name of ['quantity', 'project_scale']) {
      const value = String(values[name] || '').trim();
      if (value && (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value)))) errors[name] = 'Enter a positive whole number or leave blank.';
    }
    const phone = String(values.phone || '').trim();
    if (phone && (!/^[+\d\s().-]+(?:\s*(?:x|ext\.?)\s*\d+)?$/i.test(phone) || (phone.match(/\d/g) || []).length < 5)) errors.phone = 'Enter a valid telephone number.';
    return errors;
  }
  function buildPayload(product, resolution, values, countries) {
    if (Object.keys(errorsFor(values, countries)).length) throw Error('Invalid form');
    const country = selectCountry(countries, values.country);
    const selected = { ...product };
    if (optional(values.quantity)) selected.quantity = Number(values.quantity);
    return {
      contract_version: '1.4', quotation_type: 'product', products: [selected],
      context: { business_context: 'hotel', business_context_source: 'product_single_context', product_contexts: ['hotel'] },
      project: { name: optional(values.project_name), scale: optional(values.project_scale) ? { type: 'exact', value: Number(values.project_scale) } : null,
        timeframe: optional(values.project_timeframe), narrative: optional(values.customer_narrative) },
      contact: { first_name: values.first_name.trim(), last_name: values.last_name.trim(), email: values.email.trim(),
        company: optional(values.company), phone: optional(values.phone), country_code: country.code, country_name: country.name,
        client_assigned_sales_office: country.assigned_sales_office },
      consent: { marketing_action: 'no_change' },
      attribution: { invocation_source: 'mailchimp', selection_source: 'mailchimp', referrer: null,
        campaign: { provider: 'mailchimp', identifier: resolution.attribution.campaign.identifier, name: optional(resolution.attribution.campaign.name) } }
    };
  }
  function validSuccess(response, body) {
    return Boolean(response.ok && body?.success === true && body.result === 'quotation_received'
      && body.contract_version === '1.4' && /^QTA-\d{8}-\d{6}$/.test(body.quotation_reference || ''));
  }
  // Only explicit pre-persistence rejections permit a retry. Everything else
  // may have committed, including a malformed HTTP 200 or a lost response.
  function submissionOutcome(response, body) {
    if (validSuccess(response, body)) return 'success';
    if (body?.success === false && ((response.status === 422 && body.result === 'validation_error')
      || (response.status === 403 && body.result === 'turnstile_failed'))) return 'retry';
    return 'ambiguous';
  }
  function submissionController({ fetcher, apiBase, status, success }) {
    let state = 'idle';
    return {
      state: () => state,
      async submit(payload, token) {
        if (state !== 'idle' || !token) return;
        state = 'submitting'; status(state);
        try {
          const { response, body } = await request(fetcher, `${apiBase}/api/v1/quotations`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ payload, turnstile_token: token })
          });
          const outcome = submissionOutcome(response, body);
          state = outcome === 'retry' ? 'idle' : outcome;
          status(state);
          if (state === 'success') success(body.quotation_reference);
        } catch (_) { state = 'ambiguous'; status(state); }
      }
    };
  }
  function loadTurnstile(win) {
    if (win.turnstile?.render) return Promise.resolve(win.turnstile);
    if (win.taExpressTurnstilePromise) return win.taExpressTurnstilePromise;
    win.taExpressTurnstilePromise = new Promise((resolve, reject) => {
      const script = win.document.createElement('script');
      script.src = TURNSTILE_URL; script.async = true;
      script.onload = () => win.turnstile?.render ? resolve(win.turnstile) : reject(Error('Verification unavailable'));
      script.onerror = () => reject(Error('Verification unavailable'));
      win.document.head.appendChild(script);
    });
    return win.taExpressTurnstilePromise;
  }
  function turnstileOptions(sitekey, callback, failure, expired) {
    return { sitekey, action: 'quotation_submit', execution: 'execute', appearance: 'interaction-only',
      callback, 'error-callback': failure, 'expired-callback': expired, 'timeout-callback': failure };
  }
  async function mount(node, win) {
    if (node.dataset.initialized) return;
    node.dataset.initialized = 'true';
    const find = selector => node.querySelector(selector);
    const form = find('[data-form]');
    const buttons = Array.from(node.querySelectorAll('[data-submit]'));
    const fields = Object.fromEntries(Array.from(node.querySelectorAll('[data-field]')).map(field => [field.dataset.field, field]));
    const values = () => Object.fromEntries(Object.entries(fields).map(([key, field]) => [key, field.value]));
    const status = message => { find('[data-status]').textContent = message; };
    const disable = (disabled, label) => buttons.forEach(button => { button.disabled = disabled; button.textContent = label; });
    const unavailable = () => { find('[data-loading]').hidden = true; form.hidden = true; find('[data-failure]').hidden = false; };
    let countries, product, resolution, widget, turnstile, token = '', waiting = false, pendingPayload;
    let editingContact = false;
    const expand = open => {
      editingContact = open;
      const missing = contactErrors(values(), countries);
      const recovering = !open && Object.keys(missing).length > 0;
      find('[data-contact-fields]').hidden = !open && !recovering;
      find('[data-contact-summary]').hidden = open;
      find('[data-contact-recovery]').hidden = !recovering;
      node.querySelectorAll('[data-contact-field]').forEach(wrapper => {
        wrapper.hidden = !open && !missing[wrapper.dataset.contactField];
      });
      const toggle = find('[data-contact-toggle]'); toggle.textContent = open ? 'Done' : 'Edit details'; toggle.setAttribute('aria-expanded', String(open));
      const v = values(); const country = selectCountry(countries, v.country);
      find('[data-contact-summary]').textContent = [[v.first_name.trim(), v.last_name.trim()].filter(Boolean).join(' '), optional(v.company), v.email.trim(), country?.name].filter(Boolean).join(' · ');
    };
    const showErrors = errors => {
      Object.entries(fields).forEach(([name, field]) => {
        const message = errors[name] || ''; field.setAttribute('aria-invalid', String(Boolean(message)));
        const target = find(`[data-error="${name}"]`); if (target) { target.textContent = message; target.hidden = !message; }
      });
      expand(editingContact || Boolean(errors.phone));
      const first = Object.keys(errors)[0]; if (first) fields[first]?.focus();
    };
    const controller = submissionController({ fetcher: win.fetch.bind(win), apiBase: node.dataset.apiBase,
      status(state) {
        waiting = false;
        disable(state !== 'idle', state === 'submitting' ? 'Submitting…' : 'Request Quote');
        form.setAttribute('aria-busy', String(state === 'submitting'));
        status(state === 'ambiguous' ? AMBIGUOUS : state === 'idle' ? 'Review your details and verification, then try again.' : state === 'submitting' ? 'Submitting your quotation…' : '');
        if (state === 'idle') { token = ''; turnstile?.reset(widget); }
      }, success(reference) {
        form.hidden = true; find('[data-success]').hidden = false;
        find('[data-reference]').textContent = reference; find('[data-success-heading]').focus();
      }
    });
    const verificationFailure = () => {
      token = '';
      if (controller.state() !== 'idle') return;
      waiting = false; pendingPayload = null; disable(false, 'Request Quote');
      status('Verification was unsuccessful. Please try again.');
      if (widget !== undefined) turnstile?.reset(widget);
    };
    // Bind before asynchronous setup: native form navigation is never allowed.
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (!resolution || !turnstile || widget === undefined || waiting || controller.state() !== 'idle') return;
      const errors = errorsFor(values(), countries); showErrors(errors);
      if (Object.keys(errors).length) return;
      pendingPayload = buildPayload(product, resolution, values(), countries);
      waiting = true; disable(true, 'Verifying…'); status('Verifying your request…');
      if (token) controller.submit(pendingPayload, token);
      else { try { turnstile.execute(widget); } catch (_) { verificationFailure(); } }
    });
    try {
      if (node.dataset.apiBase !== API_BASE || !node.dataset.siteKey) throw Error('Invalid configuration');
      const link = invocation(win.location.search);
      countries = countryIndex();
      product = await lookupProduct(win.fetch.bind(win), win.location.origin, node.dataset.shopifyRoot, link.product);
      resolution = await resolveContact(win.fetch.bind(win), node.dataset.apiBase, link);
      find('[data-product-name]').textContent = product.name;
      for (const name of ['first_name','last_name','email','company']) fields[name].value = resolution.contact[name] || '';
      countries.sort((a,b) => a.name.localeCompare(b.name)).forEach(country => {
        const option = win.document.createElement('option'); option.value = country.code; option.textContent = country.name; fields.country.appendChild(option);
      });
      fields.country.value = selectCountry(countries, node.dataset.initialCountry)?.code || '';
      expand(false);
      find('[data-contact-toggle]').addEventListener('click', () => {
        if (find('[data-contact-toggle]').getAttribute('aria-expanded') === 'false') expand(true);
        else { expand(false); showErrors(contactErrors(values(), countries)); }
      });
      find('[data-loading]').hidden = true; form.hidden = false;
      disable(true, 'Loading verification…');
      try {
        turnstile = await loadTurnstile(win);
        widget = turnstile.render(find('[data-turnstile]'), turnstileOptions(node.dataset.siteKey, value => {
          token = value; if (waiting && pendingPayload) controller.submit(pendingPayload, token);
        }, verificationFailure, () => { token = ''; if (waiting) verificationFailure(); }));
        disable(false, 'Request Quote');
      } catch (_) { status('Verification could not be loaded. Please refresh and try again.'); }
    } catch (_) { unavailable(); }
  }
  return { invocation, lookupProduct, validResolution, resolveContact, selectCountry, countryIndex, normaliseCountry,
    COUNTRY_CODES, US_OFFICE_CODES, contactErrors, errorsFor, buildPayload, validSuccess, submissionOutcome,
    submissionController, turnstileOptions, mount, API_BASE, AMBIGUOUS };
});
